const COLLECTION_FORMAT = Object.freeze({
  csv: collection => collection.map(String).join(","),
  multi: collection => collection.map(String),
  pipe: collection => collection.map(String).join("|"),
  ssv: collection => collection.map(String).join(" "),
  tsv: collection => collection.map(String).join("\\t"),
});
function isIterable(value) {
  return (
    value !== undefined &&
    value !== null &&
    typeof value !== "string" &&
    typeof value[Symbol.iterator] === "function"
  );
}
/**
 * Convert a iterable value to either `object` or `array`.
 * @param iterable The value that is potentially iterable.
 * @returns The value with type that can be converted to JSON.
 */
export function jsonify(iterable) {
  // recursively investigate for an array that may potentially
  // contain an object.
  if (Array.isArray(iterable)) {
    return iterable.map(jsonify);
  }
  if (!isIterable(iterable)) {
    // for an object that is not iterable, recursively convert
    // in case there other variables that may be iterables.
    if (typeof iterable === "object" && iterable !== null) {
      const result = {};
      Object.entries(iterable).forEach(([key, value]) => {
        result[key] = jsonify(value);
      });
      return result;
    }
    return iterable;
  }
  // though `Map` is an iterable, we want to convert it as
  // an object rather than an array of key-pairs.
  if (iterable instanceof Map) {
    const result = {};
    iterable.forEach((value, key) => {
      result[jsonify(key) + ""] = jsonify(value);
    });
    return result;
  }
  // other iterables will be treated as an array.
  return [...iterable];
}
/**
 * Given an object, sanitize it with following:
 *  - Convert any iterable value to a valid JSON value.
 *  - If the key is defined in `joiner`, then join them with
 *    assigned collection format.
 * @param params The query parameter passed in by the client.
 * @param joiner The object mapping query parameter key with a collection format.
 * @returns A valid JSON object of query param; usable for `fetch` API.
 */
export function sanitize(params, joiner = {}) {
  const result = {};
  Object.keys(params).forEach(key => {
    const iterable = params[key];
    const value = jsonify(iterable);
    const format = joiner[key];
    if (typeof format === "string" && Array.isArray(value)) {
      const join = COLLECTION_FORMAT[format] || COLLECTION_FORMAT.csv;
      result[key] = join(value);
    } else if (typeof value !== "function") {
      result[key] = value;
    }
  });
  return result;
}
